import {getResolver} from './resourcePluginRegistry';
import {LOOKUP_LIMIT} from './resolutionUtils';

/**
 * Resolve one lookup, over a single kind or over several in order.
 *
 * A request can name something without saying what kind of thing it is -
 * "edit Delhi" names a Site, an SSID or an Access Point, and only looking
 * settles it. Asking for each kind as its own command costs a round trip
 * apiece, so the agent may send an ordered list instead and have it answered
 * once.
 *
 * Ordered, and by default stopped at the first kind that has anything: the
 * agent puts the likeliest kind first, so the usual outcome is a single
 * request and kinds after a match are never asked for.
 *
 * `combine_kinds` asks for every kind instead. A name that exists as both a
 * Site and an Access Point is genuinely ambiguous, and stopping early
 * answers it by accident of ordering - so when the agent means to put the
 * choice to the user, it says so and gets all of them back, still in the
 * order it asked for.
 *
 * A command naming one kind takes exactly the path it always has, including
 * the error it raises for a kind nothing can resolve.
 *
 * @param {object} params resolve_entities parameters
 * @param {object} pageContext registered page context
 * @param {object} envelope request/session correlation
 * @return {Promise<{candidates: object[]}>} matching candidates
 */
export async function resolveEntities(params = {}, pageContext = {}, envelope = {}) {
	const ordered = Array.isArray(params.entity_types)
		? params.entity_types.filter((kind) => typeof kind === 'string' && kind)
		: [];
	if (ordered.length === 0) {
		const resolver = getResolver(params.entity_type);
		if (!resolver) throw new Error('unknown_resolver');
		return resolver(params, pageContext, envelope);
	}
	const combine = params.combine_kinds === true;
	let resolved = false;
	let answered = false;
	let incomplete = false;
	const combined = [];
	for (const kind of ordered) {
		const resolver = getResolver(kind);
		if (!resolver) continue;
		resolved = true;
		// One kind failing is not the lookup failing. Asking about several
		// kinds means asking places that may not answer - a Site-scoped
		// inventory with no Site in play, say - and one of those refusing
		// must not lose the kinds that did answer.
		let candidates = [];
		try {
			const result = await resolver(
				{...params, entity_type: kind},
				pageContext,
				envelope,
			);
			candidates = Array.isArray(result?.candidates) ? result.candidates : [];
			answered = true;
		} catch (error) {
			if (!combine) throw error;
			// Missing scope is a known precondition, not a service outage.
			// A failed service cannot safely disappear from an ambiguity.
			if (!['site_context_required', 'ap_context_required'].includes(error?.message)) {
				incomplete = true;
			}
			continue;
		}
		if (!combine && candidates.length > 0) return {candidates};
		combined.push(...candidates);
	}
	if (!resolved) throw new Error('unknown_resolver');
	if (incomplete) throw new Error('lookup_incomplete');
	if (!answered) throw new Error('lookup_scope_required');
	return {candidates: combine ? combined.slice(0, LOOKUP_LIMIT) : []};
}
